import { Html, Panel } from "@mixitone/components";
import { TournamentCompetition } from "@mixitone/models";
import { ApplicationView } from "@mixitone/mvc";
import { formatDate } from "@mixitone/util";
import clsx from "clsx";
import dayjs from "dayjs";
import React from "react";
import { useNavigate } from "react-router-dom";
import { DashLayout } from "../../Components/DashLayout";
import { injectParams } from "../../lib/injectParams";
import { TournamentPageController } from "./TournamentPageController";

interface InjectedProps {
  params: {
    portalSlug: string;
  };
}

const ControlledTournamentPage: React.FC<InjectedProps> = (props) => {
  const controller = TournamentPageController.use();
  const navigate = useNavigate();
  const { tournament, competitions, loading, portalSlug, bannerUrl } = controller.state;

  const handleRegister = (competition: TournamentCompetition) => {
    if (!competition.id) return;
    navigate(`/tournament/${portalSlug}/register/${competition.id}`);
  };

  if (loading) {
    return (
      <DashLayout title="Loading...">
        <div className="flex flex-col gap-6 pb-6">
          <div className="mb-3 mt-1 flex h-4 animate-pulse gap-2">
            <div className="w-32 rounded bg-gray-200" />
            <div className="w-24 rounded bg-gray-200" />
          </div>
        </div>
      </DashLayout>
    );
  }

  if (!tournament) {
    return (
      <DashLayout title="Tournament not found">
        <div className="rounded-lg bg-white p-6 shadow-sm shadow-black/25">
          <p>This tournament does not exist or has been removed.</p>
        </div>
      </DashLayout>
    );
  }

  const showEndDate = dayjs(tournament.starts_at).endOf("day").isBefore(dayjs(tournament.ends_at));

  return (
    <DashLayout title={tournament.name}>
      <div className="flex flex-col gap-6 pb-6">
        <Panel className={clsx("relative p-0!", {})}>
          {bannerUrl && (
            <img
              src={bannerUrl}
              alt="Tournament banner"
              className="-translate-y-4 max-h-48 w-full rounded-t-lg object-cover"
            />
          )}
          <div className="space-y-2 px-4 pb-4">
            <p className="text-gray-600">
              Date: {formatDate(tournament.starts_at)} {dayjs(tournament.starts_at).format("h:mma")}
              {" - "}
              {showEndDate && formatDate(tournament.ends_at)}
              {dayjs(tournament.ends_at).format("h:mma")}
            </p>
            <Html className="prose" html={tournament.description || ""} />
          </div>
        </Panel>

        {tournament.isCompleted ? (
          <Panel title="This tournament has finished" titleClassName="font-semibold">
            <p className="text-slate-600">Thanks for taking part. Registration is closed.</p>
          </Panel>
        ) : (
          <Panel title="Register for a competition" titleClassName="font-semibold">
            <div className="divide-y">
              {competitions.map((competition) => (
                <div
                  key={competition.id}
                  className="flex cursor-pointer items-center justify-between p-4 hover:bg-gray-100"
                  onClick={() => handleRegister(competition)}
                >
                  <div>
                    <div className="text-lg font-medium text-slate-800 underline">{competition.name}</div>
                    <div className="mt-1 text-gray-600">
                      {competition.entry_type === "doubles" ? "Doubles" : "Singles"} Competition
                    </div>
                  </div>
                  <div>
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="24"
                      height="24"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      className="h-4 w-4"
                    >
                      <path d="m9 18 6-6-6-6"></path>
                    </svg>
                  </div>
                </div>
              ))}
              {competitions.length === 0 && <p>No competitions are currently available for registration.</p>}
            </div>
          </Panel>
        )}
      </div>
    </DashLayout>
  );
};

const TournamentPage = injectParams<InjectedProps>(
  TournamentPageController.scope(ApplicationView(ControlledTournamentPage)),
);

export { TournamentPage };
